import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import { FiStar } from 'react-icons/fi'
import { milesLabel } from '../../utils/shopLocation'
import theme from '../../theme'

// Shared layout pieces for the public /ask and /places pages.

export const ShopPage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 28px;
  padding: 0 16px 80px;
  min-height: 85vh;
  background: ${theme.colors.background};
  color: ${theme.colors.text};
`

export const Hero = styled.div`
  width: calc(100% + 32px);
  margin: 0 -16px;
  padding: 44px 16px 34px;
  background: linear-gradient(135deg, ${theme.colors.primaryDark} 0%, #1f4f9a 60%, ${theme.colors.primary} 100%);
  color: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  text-align: center;
`

export const ShopTitle = styled.h1`
  font-size: 34px;
  font-weight: 800;
  color: ${theme.colors.primaryDark};
  text-align: center;
  margin: 0;
  ${Hero} & {
    color: #fff;
  }
`

export const ShopSubtitle = styled.p`
  font-size: 16px;
  color: ${theme.colors.textMuted};
  text-align: center;
  max-width: 600px;
  margin: 0;
  ${Hero} & {
    color: rgba(255, 255, 255, 0.88);
  }
`

export const Section = styled.section`
  width: 100%;
  max-width: 1040px;
`

export const SectionHeading = styled.h2`
  font-size: 22px;
  font-weight: 800;
  margin: 0 0 14px;
  color: ${theme.colors.primaryDark};
`

export const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
  gap: 18px;
`

export const Crumbs = styled.nav`
  width: 100%;
  max-width: 1040px;
  font-size: 13px;
  color: ${theme.colors.textMuted};
  padding-top: 24px;
  a {
    color: ${theme.colors.primary};
    text-decoration: none;
  }
`

// A picture that never leaves an ugly hole: if the image is missing or fails to load
// (hotlinked site images sometimes do), show a tinted tile with the business's initial.
const Tile = styled.div<{ $h: number }>`
  height: ${(p) => p.$h}px;
  width: 100%;
  background: linear-gradient(135deg, ${theme.colors.primarySoft}, #d5e6f8);
  color: ${theme.colors.primary};
  font-size: 44px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
`

const Img = styled.img<{ $h: number }>`
  height: ${(p) => p.$h}px;
  width: 100%;
  object-fit: cover;
  display: block;
  background: ${theme.colors.primarySoft};
`

export const Photo: React.FC<{ src?: string; fallback?: string; name: string; height: number }> = ({ src, fallback, name, height }) => {
  const [current, setcurrent] = useState(src || fallback || '')
  const [failed, setfailed] = useState(false)
  if (!current || failed) return <Tile $h={height}>{(name || '?').charAt(0).toUpperCase()}</Tile>
  return (
    <Img
      $h={height}
      src={current}
      alt={name}
      loading="lazy"
      referrerPolicy="no-referrer"
      onError={() => {
        if (fallback && current !== fallback) setcurrent(fallback)
        else setfailed(true)
      }}
    />
  )
}

const StarsRow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  font-weight: 700;
  color: #b7791f;
  svg {
    fill: #f6ad3c;
    stroke: #f6ad3c;
  }
  small {
    color: ${theme.colors.textMuted};
    font-weight: 500;
    font-size: 12px;
  }
`

export const Rating: React.FC<{ rating?: number; count?: number }> = ({ rating, count }) =>
  typeof rating === 'number' ? (
    <StarsRow>
      <FiStar size={13} /> {rating.toFixed(1)}
      {count ? <small>({count.toLocaleString()})</small> : null}
    </StarsRow>
  ) : null

const Card = styled(Link)`
  display: flex;
  flex-direction: column;
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radius.lg};
  box-shadow: ${theme.shadow.card};
  text-decoration: none;
  color: inherit;
  overflow: hidden;
  transition: transform 0.15s, box-shadow 0.15s;
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${theme.shadow.raised};
  }
`
const CardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  padding: 12px 14px 14px;
`
const CardTitle = styled.div`
  font-weight: 800;
  font-size: 16px;
  color: ${theme.colors.primaryDark};
`
const CardMeta = styled.div`
  font-size: 12px;
  color: ${theme.colors.textMuted};
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`
const CardText = styled.div`
  font-size: 13px;
  color: ${theme.colors.text};
  line-height: 1.4;
`

export const BusinessCard: React.FC<{ b: any }> = ({ b }) => (
  <Card to={`/places/${b.category}/${b.slug}`}>
    <Photo src={b.image} name={b.name} height={150} />
    <CardBody>
      <CardTitle>{b.name}</CardTitle>
      <CardMeta>
        <span>{[b.categorylabel, b.city].filter(Boolean).join(' · ')}</span>
        {b.distancekm !== undefined && <span>{milesLabel(b.distancekm)} away</span>}
      </CardMeta>
      <Rating rating={b.rating} count={b.ratingcount} />
      {b.description && <CardText>{b.description.length > 100 ? `${b.description.slice(0, 97)}...` : b.description}</CardText>}
    </CardBody>
  </Card>
)

export const CategoryCard: React.FC<{ c: any }> = ({ c }) => (
  <Card to={`/places/${c.key}`}>
    <Photo src={c.image} name={c.label} height={110} />
    <CardBody>
      <CardTitle>{c.label}</CardTitle>
      <CardMeta>{c.count} local {c.count === 1 ? 'business' : 'businesses'}</CardMeta>
    </CardBody>
  </Card>
)
